import { createFileRoute, Link } from "@tanstack/react-router";
import { useVocabularyStatistics } from "../../hooks/useVocabularyStatistics";
import { useQueryClient } from "@tanstack/react-query";
import {
  removeQueryData,
  useVocabularyTrainerVocabularyList,
} from "../../hooks/useVocabularyTrainerVocabularies";
import { useEffect, useMemo, useState } from "react";
import { VocabularyDto } from "../../hooks/usePaginatedVocabularyList";
import {
  useMarkVocabularyAsForgotten,
  useMarkVocabularyAsLearned,
} from "../../hooks/useMarkVocabularyAsLearned";
import useTranslation from "../../hooks/useTranslation";
import {
  formatVocabularyCreatedAt,
  loadDirectionPreferences,
  saveDirectionPreferences,
  DirectionPreference,
} from "../../utils/vocabularyProgress";
import { useLoginStatus } from "../../hooks/useLoginStatus";
import { resolveTrainerCardLabels } from "../../utils/vocabularyTrainerCardLabels";
import { VocabularyPronunciationButton } from "../../components/VocabularyPronunciationButton";
import { shouldShowSourceTextLink } from "../../utils/shouldShowSourceTextLink";
import { LanguageHelperPanel } from "../../components/LanguageHelperPanel";
import LoadingScreen from "../../components/LoadingScreen";

export const Route = createFileRoute("/app/vocabulary_training_mode")({
  component: VocabularyTrainerPage,
  validateSearch: (search: Record<string, unknown>) => {
    const parsedGeneratedTextId = Number(search.generatedTextId);
    const generatedTextId = Number.isFinite(parsedGeneratedTextId)
      ? parsedGeneratedTextId
      : undefined;
    return {
      textAge: Number(search.textAge),
      maxStreak: Number(search.maxStreak),
      totalVocabularies: Number(search.totalVocabularies),
      knownVocabularies: Number(search.knownVocabularies),
      generatedTextId,
    };
  },
});

type FieldVisibility = {
  showTranslation: boolean;
  showMnemonic: boolean;
  showActions: boolean;
};

function VocabularyTrainerPage() {
  const vocabularyStatistics = useVocabularyStatistics();
  const { translate } = useTranslation();
  const loginStatus = useLoginStatus();
  const currentUserId =
    loginStatus.isSuccess && loginStatus.data.state === "logged in"
      ? loginStatus.data.userId
      : null;

  const queryClient = useQueryClient();

  const searchParams = Route.useSearch();

  const fieldVisibility: FieldVisibility = {
    showMnemonic: false,
    showTranslation: false,
    showActions: false,
  };
  const [directionPreferences, setDirectionPreferences] = useState(() =>
    loadDirectionPreferences(),
  );
  const [activeHelperVocabularyId, setActiveHelperVocabularyId] = useState<
    number | null
  >(null);
  const globalDirection: DirectionPreference =
    directionPreferences["*"] ?? "WORD_TO_TRANSLATION";
  const translationDirection = globalDirection === "TRANSLATION_TO_WORD";
  const vocabularyList = useVocabularyTrainerVocabularyList({
    ...searchParams,
    generatedTextId: searchParams.generatedTextId,
    userId: currentUserId ?? undefined,
    preferredDirection: globalDirection,
  });
  const toggleGlobalDirection = () => {
    setDirectionPreferences((prev) => {
      const current = prev["*"] ?? "WORD_TO_TRANSLATION";
      const next: DirectionPreference =
        current === "WORD_TO_TRANSLATION"
          ? "TRANSLATION_TO_WORD"
          : "WORD_TO_TRANSLATION";
      const updated = { ...prev, "*": next };
      saveDirectionPreferences(updated);
      return updated;
    });
  };

  const trainerVocabularies = vocabularyList.isSuccess ? vocabularyList.data : [];

  useEffect(() => {
    if (!vocabularyList.isSuccess || trainerVocabularies.length === 0) {
      setActiveHelperVocabularyId(null);
      return;
    }
    if (
      activeHelperVocabularyId === null ||
      !trainerVocabularies.some((item) => item.id === activeHelperVocabularyId)
    ) {
      setActiveHelperVocabularyId(trainerVocabularies[0].id);
    }
  }, [
    activeHelperVocabularyId,
    trainerVocabularies,
    vocabularyList.isSuccess,
  ]);

  const recentVocabulary = trainerVocabularies.slice(0, 5).map((item) => ({
    word: item.word,
    translation: item.translation,
  }));
  const activeHelperVocabulary = useMemo(
    () =>
      trainerVocabularies.find((item) => item.id === activeHelperVocabularyId) ??
      trainerVocabularies[0] ??
      null,
    [activeHelperVocabularyId, trainerVocabularies],
  );
  const helperWordLanguage =
    activeHelperVocabulary?.word_language ?? trainerVocabularies[0]?.word_language;
  const helperContextText =
    trainerVocabularies
      .map((item) => item.sentence?.trim())
      .filter((sentence): sentence is string => Boolean(sentence))
      .slice(0, 8)
      .join("\n\n") || undefined;

  if (!vocabularyList.isSuccess) {
    return <LoadingScreen />;
  }

  return (
    <>
      <hr className="border-t border-gray-300 w-full" />
      {typeof searchParams.generatedTextId === "number" && (
        <div className="px-3 pt-2">
          <Link
            className="inline-flex rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
            to="/app/reeprise/$generatedTextId"
            params={{ generatedTextId: String(searchParams.generatedTextId) }}
            search={{
              chapter: undefined,
              focusWord: undefined,
              focusSentence: undefined,
              focusToken: undefined,
            }}
          >
            {translate("trainer_back_to_text_details")}
          </Link>
        </div>
      )}
      <h2>Vocabularies from texts of the last {searchParams.textAge} days.</h2>
      <div className="flex items-center relative">
        <div className="ml-auto">
          <button
            className="bg-blue-400 text-white m-3 px-4 py-2 rounded-lg hover:bg-blue-600 transition duration-200"
            onClick={toggleGlobalDirection}
          >
            {translate("vocabulary_training_direction_toggle")}
          </button>
          <p className="text-right text-xs text-slate-500">
            {translate(
              translationDirection
                ? "vocabulary_preview_direction_translation_first"
                : "vocabulary_preview_direction_word_first",
            )}
          </p>
        </div>
      </div>
      <hr className="border-t border-gray-300 w-full" />
      <div className="px-3 pt-3">
        <LanguageHelperPanel
          generatedTextId={searchParams.generatedTextId}
          wordLanguage={helperWordLanguage}
          contextText={helperContextText}
          recentVocabulary={recentVocabulary}
          boundVocabulary={
            activeHelperVocabulary
              ? {
                  word: activeHelperVocabulary.word,
                  sentence: activeHelperVocabulary.sentence,
                }
              : null
          }
        />
      </div>
      <div className=" px-3 pt-1 pb-1 flex">
        <div className="flex space-x-3">
          <p>streaks:</p>
          <p className="pl-2 ml-auto">
            {">5"}: {vocabularyStatistics.data?.streak_gt5}
          </p>
          <p className="pl-2 ml-auto">
            {"5"}: {vocabularyStatistics.data?.streak_5}
          </p>
          <p className="pl-2 ml-auto">
            {"4"}: {vocabularyStatistics.data?.streak_4}
          </p>
          <p className="pl-2 ml-auto">
            {"3"}: {vocabularyStatistics.data?.streak_3}
          </p>
          <p className="pl-2 ml-auto">
            {"2"}: {vocabularyStatistics.data?.streak_2}
          </p>
          <p className="pl-2 ml-auto">
            {"1"}: {vocabularyStatistics.data?.streak_1}
          </p>
          <p className="pl-2 ml-auto">
            {"0"}: {vocabularyStatistics.data?.streak_0}
          </p>
        </div>
      </div>
      <ul className="bg-white p-2 ">
        {trainerVocabularies.map((vocabulary) => {
          return (
            <VocabularyItem
              key={vocabulary.id}
              vocabulary={vocabulary}
              fieldVisibility={fieldVisibility}
              onActivate={(activeVocabularyId) =>
                setActiveHelperVocabularyId(activeVocabularyId)
              }
            />
          );
        })}
        <li className="bg-blue-500 p-3 rounded-lg shadow-lg mb-2 w-full">
          <button
            className="w-full bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition duration-200"
            onClick={() => {
              removeQueryData(queryClient);
            }}
          >
            Reload
          </button>
        </li>
      </ul>
    </>
  );
}

function VocabularyItem(props: {
  vocabulary: VocabularyDto;
  fieldVisibility: FieldVisibility;
  onActivate: (vocabularyId: number) => void;
}) {
  const [tapped, setTapped] = useState(false);
  const [answerRevealed, setAnswerRevealed] = useState(false);
  const [newStreak, setNewStreak] = useState<null | number>(null);
  const { translate } = useTranslation();

  const markVocabularyAsLearned = useMarkVocabularyAsLearned();
  const markVocabularyAsForgotten = useMarkVocabularyAsForgotten();
  const id = props.vocabulary.id;
  const sourceSentence = props.vocabulary.sentence?.trim() || null;
  const sourceUsage =
    props.vocabulary.usages.find((usage) => typeof usage.text === "number") ??
    null;
  const sourceTextTarget = sourceUsage
    ? {
        generatedTextId: String(sourceUsage.text),
        chapter:
          typeof sourceUsage.chapter_counter === "number"
            ? sourceUsage.chapter_counter
            : undefined,
      }
    : null;
  const sourceLinkTarget =
    sourceSentence && sourceTextTarget ? sourceTextTarget : null;

  const { reviewDirection, questionText, answerText } = resolveTrainerCardLabels({
    vocabulary: props.vocabulary,
    pendingTranslationLabel: translate("generated_text_word_preview_translation_pending"),
  });
  const addedOnCopy =
    formatVocabularyCreatedAt(props.vocabulary.created_at) ??
    translate("progress_never");

  return (
    <li className="group text-left bg-slate-100 relative mb-4 p-2 rounded-md">
      <div
        className={"bg-slate-200 rounded-md mb-2 p-2 cursor-pointer"}
        onClick={() => {
          props.onActivate(props.vocabulary.id);
          setTapped(!tapped);
        }}
      >
        <p className="text-xs uppercase tracking-wide text-slate-500 mb-1 px-1">
          {translate(
            reviewDirection === "TRANSLATION_TO_WORD"
              ? "vocabulary_preview_direction_question_label_native"
              : "vocabulary_preview_direction_question_label_foreign",
          )}
        </p>
        <div className="flex items-start justify-between gap-2 px-1">
          <p className="text-lg font-semibold leading-snug my-0">{questionText}</p>
          <VocabularyPronunciationButton
            vocabulary={props.vocabulary}
            className="h-8 w-8 shrink-0"
          />
        </div>
        <p className="mt-2 px-1 text-xs text-slate-500">
          {translate("vocabulary_guess_prompt")}
        </p>
        <p className="px-1 text-xs text-slate-500">
          {translate("vocabulary_label_added_on")}: {addedOnCopy}
        </p>
        <p
          className={`px-1 text-lg font-medium leading-snug mt-0 transition ${
            answerRevealed || props.fieldVisibility.showTranslation
              ? "text-slate-600"
              : "blur-sm select-none"
          }`}
        >
          {answerText}
        </p>
        <button
          type="button"
          className="mx-1 mt-2 rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-500"
          onClick={(event) => {
            event.stopPropagation();
            setAnswerRevealed((prev) => !prev);
          }}
        >
          {(answerRevealed || props.fieldVisibility.showTranslation)
            ? translate("vocabulary_guess_hide_answer")
            : translate("vocabulary_guess_reveal_answer")}
        </button>
        {(tapped || props.fieldVisibility.showTranslation) && (
          <p className="text-xs text-slate-500 mt-2 px-1">
            {translate("vocabulary_guess_trainer_hint")}
          </p>
        )}
      </div>
      {(tapped || props.fieldVisibility.showActions) && (
        <div className="my-2 space-y-2">
          <div className="flex justify-between">
            {newStreak === null && (
              <button
                className="rounded-md p-2 bg-slate-200"
                onClick={() => {
                  setNewStreak(props.vocabulary.winning_streak + 1);
                  markVocabularyAsLearned.mutateAsync({
                    vocabularyId: id,
                    reviewDirection,
                  });
                }}
              >
                I know it
              </button>
            )}
            <p>
              You had a streak of {props.vocabulary.winning_streak} on this word.
            </p>
            {newStreak !== null && <p>Now your streak is {newStreak}.</p>}
            {newStreak === null && (
              <button
                className="rounded-md p-2 bg-slate-200"
                onClick={() => {
                  setNewStreak(0);
                  markVocabularyAsForgotten.mutateAsync({
                    vocabularyId: id,
                    reviewDirection,
                  });
                }}
              >
                I don't know it
              </button>
            )}
          </div>
          {shouldShowSourceTextLink({
            sourceSentence,
            sourceTextTarget,
          }) && sourceLinkTarget && (
            <Link
              to="/app/reeprise/$generatedTextId"
              params={{ generatedTextId: sourceLinkTarget.generatedTextId }}
              search={{
                chapter: sourceLinkTarget.chapter,
                focusWord: sourceSentence ? props.vocabulary.word : undefined,
                focusSentence: sourceSentence ?? undefined,
                focusToken: sourceSentence ? props.vocabulary.id : undefined,
              }}
              className="inline-flex rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
            >
              {translate("trainer_open_source_text")}
            </Link>
          )}
        </div>
      )}
    </li>
  );
}
